import { useQueryClient } from 'react-query';
import { merge } from 'lodash';
import {
  useCreateUserShippingAddressMutation,
  useDeleteUserShippingAddressMutation,
  useSetDefaultUserShippingAddressMutation,
  useUpdateUserShippingAddressMutation
} from 'lib/home/generated/queries';
import { User } from 'lib/home/generated/types/api';

export interface UseMutationCallbackOptions {
  onMutate?: () => void;
  onError?: () => void;
  onSuccess?: () => void;
}

const QUERY_KEY = ['MyShippingAddresses'];

export const useShippingAddressCreateMutation = (options?: UseMutationCallbackOptions) => {
  const queryClient = useQueryClient();
  const { onMutate, onError, onSuccess } = options || {};

  return useCreateUserShippingAddressMutation({
    onMutate: async ({ createAddressInput }) => {
      // Cancel any outgoing re-fetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(QUERY_KEY);

      // Snapshot the previous value
      const prevData: { me: User } = queryClient.getQueryData(QUERY_KEY);

      const updatedShippingAddresses = [...prevData.me.shipping_addresses, createAddressInput];

      // Optimistically update the shipping addresses data
      queryClient.setQueryData(
        QUERY_KEY,
        merge({}, prevData, { me: { shipping_addresses: updatedShippingAddresses } })
      );

      if (onMutate) onMutate();

      // Return a context object with the snap-shotted value
      return { prevData };
    },
    onError: (err, data, context: any) => {
      // Restore the previous data on error
      queryClient.setQueryData(QUERY_KEY, context.prevData);

      if (onError) onError();
    },
    onSettled: async data => {
      // Re-fetch the data after the mutation has settled
      await queryClient.refetchQueries(QUERY_KEY);

      if (onSuccess) onSuccess();
    }
  });
};

export const useShippingAddressUpdateMutation = (options?: UseMutationCallbackOptions) => {
  const queryClient = useQueryClient();
  const { onMutate, onError, onSuccess } = options || {};

  return useUpdateUserShippingAddressMutation({
    onMutate: async ({ updateAddressInput }) => {
      // Cancel any outgoing re-fetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(QUERY_KEY);

      // Snapshot the previous value
      const prevData: { me: User } = queryClient.getQueryData(QUERY_KEY);

      const updatedShippingAddresses = [...prevData.me.shipping_addresses];
      const updatedItemIndex = prevData.me.shipping_addresses.findIndex(
        shippingAddress => shippingAddress.id === updateAddressInput.id
      );

      updatedShippingAddresses[updatedItemIndex] = merge(
        {},
        updatedShippingAddresses[updatedItemIndex],
        updateAddressInput
      );

      // Optimistically update the shipping addresses data
      queryClient.setQueryData(
        QUERY_KEY,
        merge({}, prevData, { me: { shipping_addresses: updatedShippingAddresses } })
      );

      if (onMutate) onMutate();

      // Return a context object with the snap-shotted value
      return { prevData };
    },
    onError: (err, data, context: any) => {
      // Restore the previous data on error
      queryClient.setQueryData(QUERY_KEY, context.prevData);

      if (onError) onError();
    },
    onSettled: async data => {
      // Re-fetch the data after the mutation has settled
      await queryClient.refetchQueries(QUERY_KEY);

      if (onSuccess) onSuccess();
    }
  });
};

export const useShippingAddressDeleteMutation = (options?: UseMutationCallbackOptions) => {
  const queryClient = useQueryClient();
  const { onMutate, onError, onSuccess } = options || {};

  return useDeleteUserShippingAddressMutation({
    onMutate: async ({ addressId }) => {
      // Cancel any outgoing re-fetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(QUERY_KEY);

      // Snapshot the previous value
      const prevData: { me: User } = queryClient.getQueryData(QUERY_KEY);

      const updatedShippingAddresses = prevData.me.shipping_addresses.filter(
        shippingAddress => shippingAddress.id !== addressId
      );

      // Optimistically remove the shipping address
      queryClient.setQueryData(
        QUERY_KEY,
        merge({}, prevData, { me: { shipping_addresses: updatedShippingAddresses } })
      );

      if (onMutate) onMutate();

      // Return a context object with the snap-shotted value
      return { prevData };
    },
    onError: (err, data, context: any) => {
      // Restore the previous data on error
      queryClient.setQueryData(QUERY_KEY, context.prevData);

      if (onError) onError();
    },
    onSettled: () => {
      // Re-fetch the data after the mutation has settled
      queryClient.refetchQueries(QUERY_KEY);

      if (onSuccess) onSuccess();
    }
  });
};

export const useShippingAddressSetDefaultMutation = (options?: UseMutationCallbackOptions) => {
  const queryClient = useQueryClient();
  const { onMutate, onError, onSuccess } = options || {};

  return useSetDefaultUserShippingAddressMutation({
    onMutate: async ({ addressId }) => {
      // Cancel any outgoing re-fetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(QUERY_KEY);

      // Snapshot the previous value
      const prevData: { me: User } = queryClient.getQueryData(QUERY_KEY);

      if (!prevData?.me?.shipping_addresses) {
        return { prevData };
      }

      const updatedShippingAddresses = [...prevData.me.shipping_addresses];
      const oldDefaultItemIndex = updatedShippingAddresses.findIndex(address => address.is_default);
      const newDefaultItemIndex = updatedShippingAddresses.findIndex(address => address.id === addressId);

      if (updatedShippingAddresses[oldDefaultItemIndex])
        updatedShippingAddresses[oldDefaultItemIndex].is_default = false;

      if (updatedShippingAddresses[newDefaultItemIndex])
        updatedShippingAddresses[newDefaultItemIndex].is_default = true;

      // Optimistically update the shipping addresses data
      queryClient.setQueryData(
        QUERY_KEY,
        merge({}, prevData, { me: { shipping_addresses: updatedShippingAddresses } })
      );

      if (onMutate) onMutate();

      // Return a context object with the snap-shotted value
      return { prevData };
    },
    onError: (err, data, context: any) => {
      // Restore the previous data on error
      queryClient.setQueryData(QUERY_KEY, context.prevData);

      if (onError) onError();
    },
    onSettled: () => {
      // Re-fetch the data after the mutation has settled
      queryClient.refetchQueries(QUERY_KEY);

      if (onSuccess) onSuccess();
    }
  });
};
